import {
  SpaceBetween,
  Box,
  CollectionPreferences,
  Link,
  Pagination,
  Table,
  TextFilter,
  Select,
  Badge,
  ButtonDropdown,
  CollectionPreferencesProps,
  ButtonDropdownProps,
  Flashbar,
  FlashbarProps,
  Multiselect,
} from '@amzn/awsui-components-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { buildAmbassadorDirectoryOptions } from '../../utils/ambassadors';
import { useUser } from '../../hooks/user';
import { useSearchParams } from 'react-router-dom';
import { UpdateAmbassadorModal } from './updateAmbassadorModal';
import { AmbassadorRecord } from '@amzn/red-velvet-api';

function representationTypesMatch(a: OptionDefinition[], b: string[]) {
  if (a?.length !== b?.length) return false;

  const expected = new Set(a.map((x) => x.label?.toUpperCase()));
  const actual = new Set(b);

  if (expected.size !== actual.size) return false;

  return Array.from(expected).every((val) => actual.has(val || ''));
}

export interface AmbassadorDirectoryRow {
  name: string;
  alt: string;
  description: string;
  type: string;
  size: string;
}

export function AmbassadorDirectoryTable({
  ambassadors,
  inYourArea,
  isLoading,
}: {
  ambassadors: AmbassadorRecord[];
  inYourArea?: boolean;
  isLoading?: boolean;
}) {
  const { t } = useTranslation(['ambassadors', 'translation']);
  const loggedInUser = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    contentDisplay: [
      { id: 'name', visible: true },
      { id: 'email', visible: true },
      { id: 'country', visible: true },
      { id: 'city', visible: true },
      { id: 'representationType', visible: true },
      { id: 'siteCode', visible: true },
      { id: 'registrationDate', visible: true },
      { id: 'ergs', visible: true },
      { id: 'actions', visible: true },
    ],
  });
  const [selectedCountry, setSelectedCountry] = useState<OptionDefinition | null>(null);
  const [selectedCity, setSelectedCity] = useState<OptionDefinition | null>(null);
  const [selectedSiteCode, setSelectedSiteCode] = useState<OptionDefinition | null>(null);
  const [selectedRepresentation, setSelectedRepresentation] = useState<OptionDefinition[]>([]);
  const [flashbars, setFlashbars] = useState<FlashbarProps.MessageDefinition[]>([]);

  const [showingUpdateModal, setShowingUpdateModal] = useState<boolean>(false);

  const directoryActionsForSelf = [
    {
      id: 'updateDetails',
      text: t('ambassadorDirectory.updateYourRegistrationDetails'),
      href: '#update',
    },
    {
      id: 'leave',
      text: t('ambassadorDirectory.leaveTheCommunityAmbassadorProgram'),
      href: '/ambassadors/offboarding',
    },
  ];

  const directoryActionsForOthers = [
    {
      id: 'remove',
      text: t('ambassadorDirectory.requestRemoval'),
      href: 'https://issues.amazon.com/issues/create?assignedFolder=06baf31b-5f26-4e6c-bfdf-2720f1fdb751&title=Removal+Request+-+Community+Ambassador+-+%5BCommunity+Ambassador+Name%5D&description=I+am+requesting+the+removal+of+the+following+Community+Ambassador%3A%0A%0A-+Ambassador+Name+%3D+%0A-+Ambassador+Email+Address+%3D+%0A-+Ambassador+Region+%3D+%0A-+Removal+Reason+%3D+&descriptionContentType=text%2Fplain&assigneeIdentity=kerberos%3Ahigellio%40ANT.AMAZON.COM&extensions%5Btt%5D%5Bcategory%5D=',
      external: true,
    },
  ];

  const deleteSearchParam = useCallback(
    (key: string) => {
      setSearchParams((prev) => {
        if (!prev.has(key)) return prev;
        const next = new URLSearchParams(prev);
        next.delete(key);
        return next;
      });
    },
    [setSearchParams]
  );

  const setModalVisibility = useCallback(
    (visible: boolean) => {
      setShowingUpdateModal(visible);
      if (!visible) {
        deleteSearchParam('update');
      }
    },
    [setShowingUpdateModal, deleteSearchParam]
  );

  useEffect(() => {
    setModalVisibility(searchParams.has('update'));
  }, [searchParams, setModalVisibility]);

  const filteredItems = ambassadors.filter((i) => {
    const hasSelectedSiteCodeToFilterBy = !!selectedSiteCode?.value;
    const ambassadorMatchesSelectedSiteCode = i.siteCode === selectedSiteCode?.value;

    const ambassadorIsInSameCityAsLoggedInUser = i.city === loggedInUser.employeeCity;
    const hasSelectedCountryToFilterBy = !!selectedCountry?.value;
    const ambassadorMatchesSelectedCountry = i.country === selectedCountry?.value;
    const hasSelectedCityToFilterBy = !!selectedCity?.value;
    const ambassadorMatchesSelectedCity = i.city === selectedCity?.value;

    // representation type
    const hasSelectedRepresentationType = representationTypesMatch(
      selectedRepresentation,
      i.representationTypes as string[]
    );

    return (
      // if user has selected a site code to filter by, and the current ambassador has that site code
      // or if we have not selected a site code
      ((hasSelectedSiteCodeToFilterBy && ambassadorMatchesSelectedSiteCode) ||
        !hasSelectedSiteCodeToFilterBy) &&
      // same here, checks if erg is selected and filters by it
      ((selectedRepresentation.length && hasSelectedRepresentationType) ||
        !selectedRepresentation.length) &&
      // For the "In Your Area" table we filter by the logged in user's city
      ((inYourArea && ambassadorIsInSameCityAsLoggedInUser) ||
        // we only have to check country and city if user is not in the "In Your Area" table
        // because we know the city and country is the logged in user's city and country in the "In Your Area" table
        (!inYourArea &&
          ((hasSelectedCountryToFilterBy && ambassadorMatchesSelectedCountry) ||
            !hasSelectedCountryToFilterBy) &&
          ((hasSelectedCityToFilterBy && ambassadorMatchesSelectedCity) ||
            !hasSelectedCityToFilterBy)))
    );
  });

  const { items, collectionProps, filterProps, paginationProps } = useCollection(
    filteredItems || [],
    {
      filtering: {
        filteringFunction: (item, token) => {
          if (!token) return true;
          const text = token.toLowerCase().trim();
          return (
            `${item.firstName} ${item.lastName}`.toLowerCase().includes(text) ||
            item.email?.toLowerCase().includes(text) ||
            false
          );
        },
      },
      pagination: { defaultPage: 0, pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
    }
  );

  const countryOptions = useMemo(() => {
    return buildAmbassadorDirectoryOptions(ambassadors || [], 'country');
  }, [ambassadors]);

  const cityOptions = useMemo(() => {
    return buildAmbassadorDirectoryOptions(ambassadors || [], 'city');
  }, [ambassadors]);

  const siteCodeOptions = useMemo(() => {
    return buildAmbassadorDirectoryOptions(ambassadors || [], 'siteCode');
  }, [ambassadors]);

  const onDropdownItemClick = (item: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
    if (item.detail.id === 'updateDetails') {
      setModalVisibility(true);
    }
  };

  const dataUpdatedFlash = {
    type: 'success',
    dismissible: true,
    dismissLabel: t('ambassadorDirectory.dismissMessage'),
    onDismiss: () => setFlashbars([]),
    content: (
      <span data-testid="ambassador-update-success-flashbar">
        {t('ambassadorDirectory.successfulUpdate')}
      </span>
    ),
    id: 'message_1',
  } as FlashbarProps.MessageDefinition;

  const updateErrorFlash = {
    type: 'error',
    dismissible: true,
    dismissLabel: t('ambassadorDirectory.dismissMessage'),
    onDismiss: () => setFlashbars([]),
    content: <>{t('ambassadorDirectory.errorOnUpdate')}</>,
    id: 'message_2',
  } as FlashbarProps.MessageDefinition;

  const onSuccessfulUpdate = () => {
    setFlashbars([dataUpdatedFlash]);
  };

  const onUpdateError = () => {
    setFlashbars([updateErrorFlash]);
  };

  return (
    <SpaceBetween direction="vertical" size="s">
      <Flashbar items={flashbars} />
      <UpdateAmbassadorModal
        visible={showingUpdateModal}
        setVisible={setModalVisibility}
        onSuccess={onSuccessfulUpdate}
        onError={onUpdateError}
      />
      <Table
        {...collectionProps}
        data-testid="ambassador-directory-table"
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        columnDefinitions={[
          {
            id: 'name',
            header: t('ambassadorDirectory.name'),
            cell: (item) => `${item.firstName} ${item.lastName}`,
            sortingField: 'name',
            sortingComparator: (a, b) => {
              return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
            },
            isRowHeader: true,
          },
          {
            id: 'email',
            header: t('ambassadorDirectory.email'),
            cell: (item) => <Link href="#">{item.email || '-'}</Link>,
            sortingField: 'email',
          },
          {
            id: 'country',
            header: t('ambassadorDirectory.country'),
            cell: (item) => item.country || '-',
            sortingField: 'country',
          },
          {
            id: 'city',
            header: t('ambassadorDirectory.city'),
            cell: (item) => item.city || '-',
            sortingField: 'city',
          },
          {
            id: 'representationType',
            header: t('ambassadorDirectory.representationType'),
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xxs">
                {item.representationTypes?.map((representation) => {
                  // undefined color is gray which is what we want for SITE
                  const color =
                    representation === 'SITE'
                      ? undefined
                      : representation === 'ELG'
                        ? 'green'
                        : 'blue';
                  return <Badge color={color}>{representation}</Badge>;
                })}
              </SpaceBetween>
            ),
            sortingField: 'representationType',
            sortingComparator: (a, b) => {
              const aSorted = [...(a.representationTypes || [])].sort((x, y) => x.localeCompare(y));
              const bSorted = [...(b.representationTypes || [])].sort((x, y) => x.localeCompare(y));
              const minLen = Math.min(aSorted.length, bSorted.length);
              for (let i = 0; i < minLen; i++) {
                const cmp = aSorted[i].localeCompare(bSorted[i]);
                if (cmp !== 0) return cmp;
              }
              return aSorted.length - bSorted.length;
            },
          },
          {
            id: 'siteCode',
            header: t('ambassadorDirectory.siteCode'),
            cell: (item) => item.siteCode || '-',
            sortingField: 'siteCode',
          },
          {
            id: 'registrationDate',
            header: t('ambassadorDirectory.registrationDate'),
            cell: (item) => item.lastOnboardedDate.toDateString() || '-',
            sortingField: 'registrationDate',
            sortingComparator: (a, b) =>
              a.lastOnboardedDate.getTime() - b.lastOnboardedDate.getTime(),
          },
          {
            id: 'actions',
            header: t('ambassadorDirectory.actions'),
            cell: (item) => (
              <ButtonDropdown
                items={
                  item.employeeId === loggedInUser.employeeId
                    ? directoryActionsForSelf
                    : directoryActionsForOthers
                }
                onItemClick={onDropdownItemClick}
                expandToViewport
                ariaLabel="Control instance"
                variant="icon"
              />
            ),
            minWidth: 170,
          },
        ]}
        loading={isLoading}
        enableKeyboardNavigation
        items={items}
        filter={
          <SpaceBetween direction="horizontal" size="s" alignItems="end">
            <TextFilter
              {...filterProps}
              filteringPlaceholder={t('ambassadorDirectory.searchByNameOrAlias')}
              data-testid="ambassador-directory-name-search-input"
            />
            {!inYourArea && (
              <>
                <Select
                  data-directory-dropdown
                  selectedOption={selectedCountry}
                  placeholder={t('ambassadorDirectory.country')}
                  inlineLabelText={t('ambassadorDirectory.country')}
                  onChange={({ detail }) => {
                    detail.selectedOption.value !== selectedCountry?.value
                      ? setSelectedCountry(detail.selectedOption)
                      : setSelectedCountry(null);
                  }}
                  options={countryOptions}
                />
                <Select
                  selectedOption={selectedCity}
                  placeholder={t('ambassadorDirectory.city')}
                  inlineLabelText={t('ambassadorDirectory.city')}
                  onChange={({ detail }) => {
                    detail.selectedOption.value !== selectedCity?.value
                      ? setSelectedCity(detail.selectedOption)
                      : setSelectedCity(null);
                  }}
                  options={cityOptions}
                />
              </>
            )}
            <Select
              selectedOption={selectedSiteCode}
              inlineLabelText={t('ambassadorDirectory.siteCode')}
              placeholder={t('ambassadorDirectory.siteCode')}
              onChange={({ detail }) => {
                detail.selectedOption.value !== selectedSiteCode?.value
                  ? setSelectedSiteCode(detail.selectedOption)
                  : setSelectedSiteCode(null);
              }}
              options={siteCodeOptions}
            />
            <Multiselect
              inlineTokens
              selectedOptions={selectedRepresentation}
              inlineLabelText={t('ambassadorDirectory.representation')}
              placeholder={t('ambassadorDirectory.representation')}
              onChange={({ detail }) => {
                setSelectedRepresentation([...detail.selectedOptions]);
              }}
              options={[
                { label: 'Site', value: 'Site' },
                { label: 'Team', value: 'Team' },
                { label: 'ELG', value: 'ELG' },
              ]}
            />
          </SpaceBetween>
        }
        columnDisplay={preferences.contentDisplay}
        preferences={
          <CollectionPreferences
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            contentDisplayPreference={{
              title: t('ambassadorDirectory.columnPreferences'),
              options: [
                { id: 'name', label: t('ambassadorDirectory.name') },
                { id: 'email', label: t('ambassadorDirectory.email') },
                { id: 'country', label: t('ambassadorDirectory.country') },
                { id: 'city', label: t('ambassadorDirectory.city') },
                { id: 'representationType', label: t('ambassadorDirectory.representationType') },
                { id: 'siteCode', label: t('ambassadorDirectory.siteCode') },
                { id: 'registrationDate', label: t('ambassadorDirectory.registrationDate') },
                { id: 'ergs', label: t('ambassadorDirectory.ELG') },
                { id: 'actions', label: t('ambassadorDirectory.actions') },
              ],
            }}
            pageSizePreference={{
              title: t('ambassadorDirectory.pageSize'),
              options: [
                { value: 10, label: `10 ${t('ambassadorDirectory.items')}` },
                { value: 20, label: `20 ${t('ambassadorDirectory.items')}` },
                { value: 30, label: `30 ${t('ambassadorDirectory.items')}` },
                { value: 40, label: `40 ${t('ambassadorDirectory.items')}` },
              ],
            }}
            confirmLabel={t('ambassadorDirectory.save')}
            cancelLabel={t('ambassadorDirectory.cancel')}
          />
        }
        loadingText={t('ambassadorDirectory.loadingResources')}
        pagination={<Pagination {...paginationProps} />}
        empty={
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b data-testid="ambassador-directory-no-resources-text">
                {t('ambassadorDirectory.noResources')}
              </b>
            </SpaceBetween>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
